import React, { useReducer, useCallback, createContext, useContext, useMemo, useState, useEffect, useRef } from 'react';
import { OnomyHubWallet, OnomyHub, OnomyHubConstants, OnomyHubUtils } from '@onomy/hub';
import { useAsyncValue, useAsyncPoll, useSubscribedValue, useAsyncStatuses, useAsyncProcess } from '@onomy/react-utils';
import { Wallet } from '@onomy/wallet';
import BigNumber, { BigNumber as BigNumber$1 } from 'bignumber.js';
import { gql, ApolloClient, InMemoryCache } from '@apollo/client';

function walletLoaderReducer(state, action) {
  switch (action.type) {
    case "READ_FAILURE":
    case "CREATE_FAILURE":
    case "SAVE_FAILURE":
    case "ERASE_FAILURE":
      return { stage: "PROBLEM", error: action.error };
    case "READ_START":
      return { stage: "READING" };
    case "READ_SUCCESS":
      return { stage: "LOADED" };
    case "READ_MISSING":
      return { stage: "NOT_FOUND" };
    case "CREATE_START":
      return { stage: "CREATING" };
    case "CREATE_SUCCESS":
      return { stage: "CREATED", mnemonic: action.mnemonic };
    case "SAVE_START":
      return { stage: "SAVING" };
    case "SAVE_SUCCESS":
      return { stage: "SAVED" };
    case "ERASE_START":
      return { stage: "ERASING" };
    case "ERASE_SUCCESS":
      return { stage: "NOT_FOUND" };
    default:
      return state;
  }
}
function useWalletLoader(wallet) {
  const [state, dispatch] = useReducer(walletLoaderReducer, { stage: "INITIAL" });
  const isLoaded = ["LOADED", "SAVED"].includes(state.stage);
  const isLoading = ["INITIAL", "READING", "CREATING"].includes(state.stage);
  const isSaving = state.stage === "SAVING";
  const isMissing = state.stage === "NOT_FOUND";
  const doRead = useCallback(async () => {
    dispatch({ type: "READ_START" });
    try {
      const found = await wallet.unlock();
      if (found) {
        dispatch({ type: "READ_SUCCESS" });
      } else {
        dispatch({ type: "READ_MISSING" });
      }
    } catch (error) {
      dispatch({ type: "READ_FAILURE", error });
    }
  }, [wallet]);
  const doCreate = useCallback(async () => {
    dispatch({ type: "CREATE_START" });
    try {
      const mnemonic = await wallet.generate();
      dispatch({ type: "CREATE_SUCCESS", mnemonic });
    } catch (error) {
      dispatch({ type: "CREATE_FAILURE", error });
    }
  }, [wallet]);
  const doSave = useCallback(async (mnemonic) => {
    dispatch({ type: "SAVE_START" });
    try {
      await wallet.loadMnemonic(mnemonic);
      dispatch({ type: "SAVE_SUCCESS" });
    } catch (error) {
      dispatch({ type: "SAVE_FAILURE", error });
    }
  }, [wallet]);
  const doErase = useCallback(async () => {
    dispatch({ type: "ERASE_START" });
    try {
      await wallet.erase();
      dispatch({ type: "ERASE_SUCCESS" });
    } catch (error) {
      dispatch({ type: "ERASE_FAILURE", error });
    }
  }, [wallet]);
  return {
    isLoaded,
    isLoading,
    isSaving,
    isMissing,
    error: "error" in state ? state.error : "",
    mnemonic: "mnemonic" in state ? state.mnemonic : "",
    doRead,
    doCreate,
    doSave,
    doErase
  };
}

function useOnomyHubState(params) {
  const { backend } = params;
  const wallet = useMemo(() => new Wallet(backend), [backend]);
  const walletLoader = useWalletLoader(wallet);
  const allWallets = useMemo(() => {
    return OnomyHubWallet.init({
      wallet,
      onomyChainId: params.onomyChainId,
      onomyChainInfo: params.onomyChainInfo,
      ethWsUrl: params.ethWsUrl
    });
  }, [wallet, params.onomyChainId, params.onomyChainInfo, params.ethWsUrl]);
  const [onomy, setOnomy] = useState(null);
  const [ethereum, setEthereum] = useState(null);
  const hubWallet = useMemo(() => {
    return {
      core: allWallets.core,
      ethereum: ethereum != null ? ethereum : allWallets.ethereum,
      onomy: onomy != null ? onomy : allWallets.cosmos
    };
  }, [allWallets, onomy, ethereum]);
  const onomyConnected = !!onomy;
  const ethereumConnected = !!ethereum;
  const onomyAvailable = useMemo(() => {
    return !!allWallets.onomyWallets.find((x) => x.isAvailable());
  }, [allWallets]);
  const ethereumAvailable = useMemo(() => {
    return !!allWallets.ethWallets.find((x) => x.isAvailable());
  }, [allWallets]);
  const initHub = useCallback(() => walletLoader.isLoaded ? OnomyHub.init({ ...params, wallet: hubWallet }) : Promise.resolve(null), [hubWallet, walletLoader.isLoaded, onomyConnected, ethereumConnected]);
  const [hub, hubStatus] = useAsyncValue(initHub, null, { resetOnChange: true });
  if (hubStatus.error)
    console.error(hubStatus.error);
  const connectOnomy = useCallback(async (onomyWallet) => {
    var _a;
    if (onomy == null ? void 0 : onomy.isConnected())
      return;
    await onomyWallet.connect();
    setOnomy(onomyWallet);
    (_a = window == null ? void 0 : window.localStorage) == null ? void 0 : _a.setItem("cosmosConnectorId", onomyWallet.getId());
  }, [onomy]);
  const connectEthereum = useCallback(async (ethWallet) => {
    var _a;
    if (ethereum == null ? void 0 : ethereum.isConnected())
      return;
    await ethWallet.connect();
    setEthereum(ethWallet);
    (_a = window == null ? void 0 : window.localStorage) == null ? void 0 : _a.setItem("ethConnectorId", ethWallet.getId());
  }, [ethereum]);
  const disconnectEthereum = useCallback(async () => {
    var _a, _b;
    (_a = window == null ? void 0 : window.localStorage) == null ? void 0 : _a.removeItem("ethConnectorId");
    (_b = window == null ? void 0 : window.localStorage) == null ? void 0 : _b.removeItem("connectorId");
    if (!(ethereum == null ? void 0 : ethereum.isConnected())) {
      setEthereum(null);
      return;
    }
    await ethereum.disconnect();
    setEthereum(null);
  }, [ethereum]);
  const disconnectOnomy = useCallback(async () => {
    var _a;
    (_a = window == null ? void 0 : window.localStorage) == null ? void 0 : _a.removeItem("cosmosConnectorId");
    if (!(onomy == null ? void 0 : onomy.isConnected())) {
      setOnomy(null);
      return;
    }
    await onomy.disconnect();
    setOnomy(null);
  }, [onomy]);
  const disconnect = useCallback(async () => {
    await Promise.all([disconnectOnomy(), disconnectEthereum()]);
  }, [disconnectEthereum, disconnectOnomy]);
  useMemo(() => {
    walletLoader.doRead();
  }, [walletLoader.doRead]);
  useEffect(() => {
    var _a, _b, _c, _d, _e, _f;
    const cosmosWalletId = ((_b = (_a = window == null ? void 0 : window.localStorage) == null ? void 0 : _a.getItem("cosmosConnectorId")) != null ? _b : "").toLowerCase();
    const ethWalletId = ((_f = (_e = (_c = window == null ? void 0 : window.localStorage) == null ? void 0 : _c.getItem("connectorId")) != null ? _e : (_d = window == null ? void 0 : window.localStorage) == null ? void 0 : _d.getItem("ethConnectorId")) != null ? _f : "").toLowerCase();
    if (cosmosWalletId) {
      const w = allWallets.onomyWallets.find((x) => x.getId() === cosmosWalletId);
      if (w)
        connectOnomy(w);
    }
    if (ethWalletId) {
      const w = allWallets.ethWallets.find((x) => x.getId() === ethWalletId);
      if (w)
        connectEthereum(w);
    }
  }, [allWallets]);
  const res = {
    wallet: {
      onomy: {
        wallets: allWallets.onomyWallets,
        connectedWallet: onomy,
        isConnected: onomyConnected,
        isAvailable: onomyAvailable,
        connect: connectOnomy,
        disconnect: disconnectOnomy
      },
      ethereum: {
        wallets: allWallets.ethWallets,
        connectedWallet: ethereum,
        isConnected: ethereumConnected,
        isAvailable: ethereumAvailable,
        connect: connectEthereum,
        disconnect: disconnectEthereum
      },
      isLoaded: walletLoader.isLoaded,
      isLoading: walletLoader.isLoading,
      isSaving: walletLoader.isSaving,
      isMissing: walletLoader.isMissing,
      error: walletLoader.error,
      mnemonic: walletLoader.mnemonic,
      disconnect,
      doCreate: walletLoader.doCreate,
      doSave: walletLoader.doSave,
      doErase: walletLoader.doErase
    },
    hub
  };
  window.onomy = res;
  return res;
}
const DEFAULT_STATE = {
  hub: null,
  wallet: {
    onomy: {
      wallets: [],
      isConnected: false,
      isAvailable: false,
      connectedWallet: null,
      disconnect: () => Promise.resolve(),
      connect: () => Promise.resolve()
    },
    ethereum: {
      wallets: [],
      isConnected: false,
      isAvailable: false,
      connectedWallet: null,
      disconnect: () => Promise.resolve(),
      connect: () => Promise.resolve()
    },
    disconnect: () => Promise.resolve(),
    isLoaded: false,
    isLoading: false,
    isSaving: false,
    isMissing: false,
    error: void 0,
    mnemonic: "",
    doCreate: async () => {
    },
    doSave: async () => {
    },
    doErase: async () => {
    }
  }
};
const OnomyHubContext = createContext(DEFAULT_STATE);
function useOnomyHub() {
  return useContext(OnomyHubContext);
}
function OnomyHubProvider({ children, ...params }) {
  const state = useOnomyHubState(params);
  return /* @__PURE__ */ React.createElement(OnomyHubContext.Provider, {
    value: state
  }, children);
}
function RequireOnomyHub({
  children,
  placeholder = null
}) {
  const { hub } = useOnomyHub();
  return hub ? /* @__PURE__ */ React.createElement(React.Fragment, null, children) : placeholder;
}

function useAddresses() {
  const { hub } = useOnomyHub();
  const getAddresses = useCallback(async () => {
    if (!hub)
      return [];
    return hub.getAddresses();
  }, [hub]);
  return useAsyncValue(getAddresses, []);
}
function useAddress(assetOrChain) {
  const { hub } = useOnomyHub();
  const getAddress = useCallback(async () => {
    if (!hub)
      return null;
    return hub.getAssetAddress(assetOrChain);
  }, [hub, assetOrChain]);
  return useAsyncPoll(getAddress, null);
}
function useDelegations() {
  const { hub } = useOnomyHub();
  const allCb = useCallback(async () => {
    if (!hub)
      return [];
    return hub.delegations.all();
  }, [hub]);
  const [initDelegataions] = useAsyncValue(allCb, []);
  const items = useMemo(() => {
    return initDelegataions.map((d) => [d.validator.address]);
  }, [initDelegataions]);
  const res = useAsyncValue(allCb, []);
  const [results, status, , setState] = res;
  const filteredResults = useMemo(() => {
    return results.filter((x) => x !== null);
  }, [results]);
  useEffect(() => {
    if (!hub)
      return void 0;
    return hub.delegations.subscribeMultiple(items, setState);
  }, [hub, items]);
  return [filteredResults, status];
}
function useDelegation(validatorAddress, fiatCurrency = OnomyHubConstants.DEFAULT_FIAT_CURRENCY) {
  const { hub } = useOnomyHub();
  const cb = useCallback(async () => {
    if (!hub)
      return null;
    return hub.delegations.get(validatorAddress, fiatCurrency);
  }, [hub, validatorAddress.id, validatorAddress.chain.name, fiatCurrency]);
  const res = useAsyncValue(cb, null);
  const [, , , setState] = res;
  useEffect(() => {
    if (!hub)
      return void 0;
    return hub.delegations.subscribe([validatorAddress, fiatCurrency], setState);
  }, [hub, validatorAddress.id, validatorAddress.chain.name, fiatCurrency]);
  return res;
}
function useDetailedBalances(balances, fiatCurrency = OnomyHubConstants.DEFAULT_FIAT_CURRENCY) {
  const { hub } = useOnomyHub();
  const cb = useCallback(async () => {
    if (!hub)
      return [];
    return hub.balances.includeDetailsMultiple(balances, fiatCurrency);
  }, [hub, balances, fiatCurrency]);
  return useSubscribedValue(useCallback((subCb) => {
    if (!hub)
      return () => {
      };
    async function handler() {
      subCb(await cb());
    }
    return hub.prices.subscribeMultiple(balances.map((b) => [b.asset, fiatCurrency]), handler);
  }, [cb, hub, balances, fiatCurrency]), []);
}
function useDetailedBalance(asset, amountFixed) {
  const { hub } = useOnomyHub();
  const balance = useMemo(() => {
    return {
      asset,
      amount: BigInt(amountFixed)
    };
  }, [amountFixed, asset.chain.name, asset.id]);
  const placeholder = useMemo(() => {
    return {
      ...OnomyHubConstants.UNKNOWN_BALANCE,
      asset: {
        ...OnomyHubConstants.UNKNOWN_BALANCE.asset,
        ...balance.asset
      },
      amount: balance.amount
    };
  }, [balance]);
  const cb = useCallback(async () => {
    if (!hub)
      return placeholder;
    return hub.balances.includeDetails(balance);
  }, [hub, balance, placeholder]);
  return useSubscribedValue(useCallback((subCb) => {
    if (!hub)
      return () => {
      };
    async function handler() {
      subCb(await cb());
    }
    return hub.prices.subscribe([balance.asset], handler);
  }, [hub, balance, cb]), placeholder);
}
function useFiatTotal(balances) {
  return useMemo(() => OnomyHubUtils.combinedFiatValue(balances), [balances]);
}
function useBalanceTotals(balances) {
  const combined = useMemo(() => OnomyHubUtils.combineBalances(balances), [balances]);
  return useDetailedBalances(combined);
}
function useSupportedAssetListFetchCb() {
  const { hub } = useOnomyHub();
  return useCallback(async () => {
    var _a;
    return (_a = hub == null ? void 0 : hub.getSupportedAssets()) != null ? _a : [];
  }, [hub]);
}
function useSupportedAssetList() {
  const cb = useSupportedAssetListFetchCb();
  return useAsyncValue(cb, []);
}
function useBalances() {
  const { hub } = useOnomyHub();
  const [assets] = useSupportedAssetList();
  const items = useMemo(() => {
    return assets.map((asset) => [asset]);
  }, [assets]);
  const cb = useCallback(async () => {
    if (!hub)
      return [];
    const balances = await hub.balances.getMultiple(items);
    return balances;
  }, [hub, items]);
  return useSubscribedValue(useCallback((subCb) => {
    if (!hub)
      return () => {
      };
    async function handler() {
      subCb(await cb());
    }
    return hub.balances.subscribeMultiple(items, handler);
  }, [cb, hub, items]), []);
}
function useSuggestedGasPricesFetchCb(asset) {
  const { hub } = useOnomyHub();
  return useCallback(async () => {
    var _a;
    if (!asset)
      return [];
    return (_a = hub == null ? void 0 : hub.suggestGasPrices({ asset })) != null ? _a : [];
  }, [hub, asset]);
}
function useSuggestedGasPrices(asset) {
  const cb = useSuggestedGasPricesFetchCb(asset);
  return useAsyncPoll(cb, []);
}
function useEthNetworkFetchCb() {
  const { hub } = useOnomyHub();
  return useCallback(async () => {
    var _a;
    const network = await ((_a = hub == null ? void 0 : hub.chains.ethereum.signer.provider) == null ? void 0 : _a.getNetwork());
    return network != null ? network : null;
  }, [hub == null ? void 0 : hub.chains.ethereum.signer]);
}
function useEthNetwork() {
  const cb = useEthNetworkFetchCb();
  return useAsyncValue(cb, null);
}
function useAssetBalance(asset, address, fiatCurrency = OnomyHubConstants.DEFAULT_FIAT_CURRENCY) {
  const { hub } = useOnomyHub();
  return useSubscribedValue(useCallback((cb) => {
    if (!hub || !asset)
      return () => {
      };
    return hub.balances.subscribe([asset, address, fiatCurrency], cb);
  }, [hub, asset == null ? void 0 : asset.chain.name, asset == null ? void 0 : asset.id, address == null ? void 0 : address.id, address == null ? void 0 : address.chain.name, fiatCurrency]), OnomyHubConstants.UNKNOWN_BALANCE);
}
function useIsAddressValid(address) {
  var _a;
  const { hub } = useOnomyHub();
  return useMemo(() => {
    if (!hub || !address)
      return false;
    return hub.isAddressValid(address);
  }, [hub, (_a = address == null ? void 0 : address.chain) == null ? void 0 : _a.name, address == null ? void 0 : address.id]);
}
function useFixedAmountInput({
  value,
  onChangeValue,
  meta
}) {
  var _a;
  const decimalPlaces = (_a = meta == null ? void 0 : meta.decimalPlaces) != null ? _a : 18;
  const withDecimal = useMemo(() => {
    const bigNum = new BigNumber(value).shiftedBy(-1 * decimalPlaces);
    if (bigNum.isNaN())
      return "";
    return bigNum.toFixed();
  }, [value, decimalPlaces]);
  const [displayValue, setDisplayValue] = useState(withDecimal);
  useEffect(() => {
    const val = new BigNumber(displayValue).shiftedBy(decimalPlaces);
    const valStr = val.isNaN() ? "" : val.toFixed(0);
    if (!val.eq(value) && onChangeValue) {
      onChangeValue(valStr);
    }
  }, [displayValue]);
  useEffect(() => {
    const val = new BigNumber(withDecimal).shiftedBy(decimalPlaces);
    const dVal = new BigNumber(displayValue).shiftedBy(decimalPlaces);
    if (!val.isNaN() && !val.eq(dVal))
      setDisplayValue(withDecimal);
    if (val.isNaN() && !dVal.isNaN())
      setDisplayValue("");
  }, [withDecimal]);
  return [displayValue, setDisplayValue];
}
function useConnectedWallet(blockchain) {
  const { wallet } = useOnomyHub();
  switch (blockchain.name) {
    case OnomyHubConstants.ETH_CHAIN.name:
      return wallet.ethereum.connectedWallet;
    case OnomyHubConstants.ONOMY_CHAIN.name:
      return wallet.onomy.connectedWallet;
    default:
      return false;
  }
}
function useWalletsForChain(blockchain) {
  const { wallet } = useOnomyHub();
  switch (blockchain.name) {
    case OnomyHubConstants.ETH_CHAIN.name:
      return {
        wallets: wallet.ethereum.wallets,
        connect: wallet.ethereum.connect,
        disconnect: wallet.ethereum.disconnect
      };
    case OnomyHubConstants.ONOMY_CHAIN.name:
      return {
        wallets: wallet.onomy.wallets,
        connect: wallet.onomy.connect,
        disconnect: wallet.onomy.disconnect
      };
    default:
      return {
        wallets: [],
        connect: () => Promise.reject(new Error(`Unknown Blockchain "${blockchain.name}"`)),
        disconnect: () => Promise.reject(new Error(`Unknown Blockchain "${blockchain.name}"`))
      };
  }
}
function useProposals() {
  const { hub } = useOnomyHub();
  const getProposals = useCallback(async () => {
    if (!hub)
      return [];
    const res = await hub.chains.onomy.client.getProposals();
    res.sort((a, b) => {
      var _a, _b, _c, _d;
      return ((_b = (_a = b.submitTime) == null ? void 0 : _a.getTime()) != null ? _b : 0) - ((_d = (_c = a.submitTime) == null ? void 0 : _c.getTime()) != null ? _d : 0);
    });
    return res;
  }, [hub]);
  return useAsyncValue(getProposals, []);
}
function useProposal(id) {
  const { hub } = useOnomyHub();
  return useSubscribedValue(useCallback((cb) => {
    var _a;
    if (hub == null ? void 0 : hub.proposals.subscribe) {
      return (_a = hub.proposals.subscribe(["onomy", id], cb)) != null ? _a : () => {
      };
    }
    return () => {
    };
  }, [id, hub == null ? void 0 : hub.proposals]), null);
}

function useGasForm(asset, gasAsset, fn, deps) {
  var _a, _b;
  const estimateGas = useCallback(fn, deps);
  const [gasEstimate, gasEstimateStatus] = useAsyncValue(estimateGas, "0");
  const [prices, pricesStatus] = useSuggestedGasPrices(asset);
  const [gasPriceIndex, setGasPriceIndex] = useState(0);
  const gasPrice = (_b = (_a = prices[gasPriceIndex]) != null ? _a : prices[0]) != null ? _b : "0";
  const setGasPrice = useCallback((newPrice) => {
    const newIndex = prices.indexOf(newPrice);
    setGasPriceIndex(newIndex === -1 ? 1 : newIndex);
  }, [prices]);
  const optionValues = useMemo(() => {
    function priceSortFn(a, b) {
      return new BigNumber(b).minus(a).toNumber();
    }
    const sorted = prices.slice().sort(priceSortFn);
    return sorted;
  }, [prices]);
  const optionAmounts = useMemo(() => {
    const tmpAsset = asset || gasAsset;
    if (!tmpAsset)
      return [];
    return optionValues.map((amountFixed) => {
      const bigNum = new BigNumber(amountFixed).times(gasEstimate || "0");
      return {
        asset: tmpAsset,
        amount: BigInt(bigNum.isNaN() ? "0" : bigNum.toFixed(0))
      };
    });
  }, [optionValues, gasAsset, asset, gasEstimate]);
  const [detailedOptionAmounts, detailedOptionAmountsStatus] = useDetailedBalances(optionAmounts);
  const options = useMemo(() => {
    const labels = ["Instant", "Fast", "Standard"];
    if (detailedOptionAmountsStatus.pending)
      return [];
    return optionValues.map((value, idx) => {
      const amount = detailedOptionAmounts[idx] || OnomyHubConstants.UNKNOWN_BALANCE;
      return {
        label: labels[idx] || "Custom",
        amount,
        value
      };
    }).reverse();
  }, [detailedOptionAmounts, detailedOptionAmountsStatus, optionValues]);
  const gasFeeTotal = useMemo(() => {
    const gasEst = new BigNumber(gasEstimate);
    const price = new BigNumber(gasPrice || 0);
    return gasEst.times(price).toFixed();
  }, [gasEstimate, gasPrice]);
  const [[gasAmount = OnomyHubConstants.UNKNOWN_BALANCE], assetStatus] = useDetailedBalances(useMemo(() => {
    return [
      {
        amount: BigInt(gasFeeTotal),
        asset: gasAsset || OnomyHubConstants.NOM_ASSET
      }
    ];
  }, [gasAsset, gasFeeTotal]));
  const loadingStatus = useAsyncStatuses([gasEstimateStatus, pricesStatus, assetStatus]);
  return {
    loadingStatus,
    detailedOptionAmountsStatus,
    gasAmount,
    gasPrice,
    options,
    setGasPrice,
    gasEstimate
  };
}

function useDelegateAssetForm({
  undelegate = false,
  validatorAddressStr: validatorAddressProp = ""
} = {}) {
  var _a;
  const { hub } = useOnomyHub();
  const [attempt, txStatus, txReset] = useAsyncProcess();
  const [validatorAddress, setValidatorAddress] = useState(validatorAddressProp);
  const [amountFixedRaw, setAmountFixedRaw] = useState("");
  const [isMaxAmount, setIsMaxAmount] = useState(false);
  const [txDetails, setTxDetails] = useState(null);
  const asset = OnomyHubConstants.NOM_ASSET;
  const gasAsset = asset;
  const [balance, balanceStatus] = useAssetBalance(asset);
  const [delegation, delegationStatus] = useDelegation({
    chain: OnomyHubConstants.ONOMY_CHAIN,
    id: validatorAddress
  });
  const [txAmount] = useDetailedBalance(asset, (_a = txDetails == null ? void 0 : txDetails.amountFixed) != null ? _a : "0");
  const amountFixed = useMemo(() => {
    var _a2;
    if (!isMaxAmount)
      return amountFixedRaw;
    if (undelegate) {
      return (_a2 = delegation == null ? void 0 : delegation.delegated.amount.toString()) != null ? _a2 : amountFixedRaw;
    }
    return balance.amount.toString();
  }, [isMaxAmount, amountFixedRaw, undelegate, balance.amount, delegation == null ? void 0 : delegation.delegated.amount]);
  const inputParams = useMemo(() => ({
    asset: OnomyHubConstants.NOM_ASSET,
    amountFixed,
    validatorAddress
  }), [amountFixed, validatorAddress]);
  const gas = useGasForm(inputParams.asset, gasAsset, async () => {
    if (!hub)
      return "0";
    return (undelegate ? hub.UndelegateAsset : hub.DelegateAsset).estimateGas(inputParams);
  }, [hub, inputParams, undelegate]);
  const [maxParams, maxParamsStatus] = useAsyncValue(useCallback(async () => {
    if (!hub || !asset || !gasAsset || !isMaxAmount || undelegate)
      return inputParams;
    if (asset.id !== gasAsset.id) {
      return {
        ...inputParams,
        amountFixed: balance.amount.toString()
      };
    }
    return hub.DelegateAsset.calculateMax({
      ...inputParams,
      amountFixed: balance.amount.toString(),
      gasMultiplier: gas.gasPrice
    });
  }, [inputParams, hub, asset, gasAsset, isMaxAmount, balance, gas.gasPrice, undelegate]), inputParams);
  const params = isMaxAmount ? maxParams : inputParams;
  const formErrors = useMemo(() => {
    if (undelegate) {
      return hub == null ? void 0 : hub.UndelegateAsset.getParamsErrors(params, delegation == null ? void 0 : delegation.delegated.amount.toString());
    }
    return hub == null ? void 0 : hub.DelegateAsset.getParamsErrors(params, balance.amount.toString());
  }, [undelegate, hub, params, balance.amount, delegation == null ? void 0 : delegation.delegated.amount]);
  const [[amount = OnomyHubConstants.UNKNOWN_BALANCE], amountStatus] = useDetailedBalances(useMemo(() => {
    return [
      {
        amount: BigInt(params.amountFixed),
        asset: asset || OnomyHubConstants.NOM_ASSET
      }
    ];
  }, [asset, params.amountFixed]));
  const loadingStatus = useAsyncStatuses([balanceStatus, delegationStatus]);
  const canSubmit = useMemo(() => {
    return !formErrors && !loadingStatus.pending && !loadingStatus.error && !!gas.gasPrice && !maxParamsStatus.pending;
  }, [formErrors, gas.gasPrice, loadingStatus, maxParamsStatus.pending]);
  const toggleMaxAmount = useCallback(() => setIsMaxAmount((x) => !x), []);
  const setAmountFixed = useCallback((amt) => {
    setIsMaxAmount(false);
    setAmountFixedRaw(amt);
  }, []);
  const reset = useCallback(() => {
    setAmountFixed("");
    setIsMaxAmount(false);
    txReset();
    setTxDetails(null);
  }, [setAmountFixed, txReset]);
  const submit = useCallback(() => {
    if (!hub || !canSubmit)
      return;
    attempt(async () => {
      const res = await (undelegate ? hub.UndelegateAsset : hub.DelegateAsset).execute({
        ...params,
        gasMultiplier: gas.gasPrice
      }, undelegate ? delegation == null ? void 0 : delegation.delegated.amount.toString() : balance.amount.toString());
      setTxDetails(res);
    });
  }, [canSubmit, attempt, undelegate, hub, params, gas.gasPrice, delegation, balance]);
  useEffect(() => {
    if (validatorAddressProp)
      setValidatorAddress(validatorAddressProp);
  }, [validatorAddressProp]);
  return {
    gas,
    formErrors,
    loadingStatus,
    txStatus,
    amountStatus,
    txDetails,
    canSubmit,
    asset,
    delegation,
    amount,
    balance,
    txAmount,
    validatorAddress,
    amountFixed: params.amountFixed,
    setValidatorAddress,
    setAmountFixed,
    toggleMaxAmount,
    reset,
    submit
  };
}

function useTransferAssetForm() {
  const { hub } = useOnomyHub();
  const [attempt, txStatus, txReset] = useAsyncProcess();
  const [destAddress, setDestAddress] = useState("");
  const [amountFixed, setAmountFixedRaw] = useState("");
  const [isMaxAmount, setIsMaxAmount] = useState(false);
  const [asset, setAsset] = useState(null);
  const [txDetails, setTxDetails] = useState(null);
  const [balance, balanceStatus] = useAssetBalance(asset);
  const inputParams = useMemo(() => ({
    asset,
    amountFixed,
    destAddress
  }), [asset, amountFixed, destAddress]);
  const [gasAsset, gasAssetStatus] = useAsyncValue(useCallback(async () => {
    if (!hub || !inputParams.asset)
      return null;
    return hub.TransferAsset.getGasAsset({
      ...inputParams,
      asset: inputParams.asset
    });
  }, [hub, inputParams.asset]), null);
  const gas = useGasForm(inputParams.asset || null, gasAsset, async () => {
    if (!hub || !inputParams.asset)
      return "0";
    return hub.TransferAsset.estimateGas({
      ...inputParams,
      asset: inputParams.asset
    });
  }, [hub, inputParams]);
  const [maxParams, maxParamsStatus] = useAsyncValue(useCallback(async () => {
    if (!hub || !asset || !gasAsset || !isMaxAmount)
      return inputParams;
    if (asset.id !== gasAsset.id) {
      return {
        ...inputParams,
        amountFixed: balance.amount.toString()
      };
    }
    return hub.TransferAsset.calculateMax({
      ...inputParams,
      amountFixed: balance.amount.toString(),
      gasMultiplier: gas.gasPrice
    });
  }, [inputParams, hub, asset, gasAsset, isMaxAmount, balance, gas.gasPrice]), inputParams);
  const params = isMaxAmount ? maxParams : inputParams;
  const [[amount = OnomyHubConstants.UNKNOWN_BALANCE], assetStatus] = useDetailedBalances(useMemo(() => {
    return [
      {
        amount: BigInt(params.amountFixed),
        asset: asset || OnomyHubConstants.NOM_ASSET
      }
    ];
  }, [asset, params.amountFixed]));
  const formErrors = useMemo(() => hub == null ? void 0 : hub.TransferAsset.getParamsErrors(params), [hub, params]);
  const loadingStatus = useAsyncStatuses([assetStatus, balanceStatus, gasAssetStatus]);
  const setAmountFixed = useCallback((amt) => {
    setIsMaxAmount(false);
    setAmountFixedRaw(amt);
  }, []);
  const toggleMaxAmount = useCallback(() => {
    setIsMaxAmount((x) => !x);
  }, []);
  const canSubmit = useMemo(() => {
    return !formErrors && !loadingStatus.pending && !loadingStatus.error && !!gas.gasPrice && !maxParamsStatus.pending;
  }, [formErrors, gas.gasPrice, loadingStatus, maxParamsStatus.pending]);
  const submit = useCallback(async () => {
    if (!hub || !asset || !canSubmit)
      return null;
    setTxDetails(null);
    let details = null;
    await attempt(async () => {
      details = await hub.TransferAsset.execute({
        ...params,
        gasMultiplier: gas.gasPrice
      });
      setTxDetails(details);
    });
    return details;
  }, [hub, canSubmit, attempt, params, gas.gasPrice, asset]);
  const reset = useCallback(() => {
    setDestAddress("");
    setAmountFixedRaw("");
    setIsMaxAmount(false);
    txReset();
    setTxDetails(null);
  }, [txReset]);
  return {
    gas,
    txStatus,
    loadingStatus,
    canSubmit,
    formErrors,
    balance,
    amount,
    asset,
    destAddress,
    isMaxAmount,
    amountFixed: params.amountFixed,
    txDetails,
    setAsset,
    setDestAddress,
    setAmountFixed,
    toggleMaxAmount,
    submit,
    reset
  };
}

function useClaimRewardForm({
  redelegate = false,
  validatorAddressStr: validatorAddressProp = ""
} = {}) {
  const { hub } = useOnomyHub();
  const [attemptTx, txStatus, txReset] = useAsyncProcess();
  const [validatorAddress, setValidatorAddress] = useState(validatorAddressProp);
  const [txDetails, setTxDetails] = useState(null);
  const asset = OnomyHubConstants.NOM_ASSET;
  const gasAsset = asset;
  const [delegation, delegationStatus] = useDelegation({
    chain: OnomyHubConstants.ONOMY_CHAIN,
    id: validatorAddress
  });
  const params = useMemo(() => ({
    asset,
    validatorAddress
  }), [asset, validatorAddress]);
  const formErrors = useMemo(() => {
    var _a;
    return (_a = redelegate ? hub == null ? void 0 : hub.RedelegateAsset : hub == null ? void 0 : hub.ClaimReward) == null ? void 0 : _a.getParamsErrors(params);
  }, [redelegate, hub, params]);
  const gas = useGasForm(params.asset, gasAsset, async () => {
    var _a;
    if (!hub)
      return "0";
    return (_a = redelegate ? hub == null ? void 0 : hub.RedelegateAsset : hub == null ? void 0 : hub.ClaimReward) == null ? void 0 : _a.estimateGas(params);
  }, [hub, params, redelegate]);
  const loadingStatus = useAsyncStatuses([delegationStatus]);
  const canSubmit = useMemo(() => {
    var _a;
    return !formErrors && !loadingStatus.pending && !loadingStatus.error && !!gas.gasPrice && ((_a = delegation == null ? void 0 : delegation.reward.amount) != null ? _a : 0) > 0;
  }, [loadingStatus, formErrors, gas.gasPrice, delegation == null ? void 0 : delegation.reward.amount]);
  const submit = useCallback(() => {
    if (!hub || !canSubmit)
      return;
    attemptTx(async () => {
      const res = await (redelegate ? hub.RedelegateAsset : hub.ClaimReward).execute({
        ...params,
        gasMultiplier: gas.gasPrice
      });
      setTxDetails(res);
    });
  }, [hub, canSubmit, attemptTx, redelegate, params, gas.gasPrice]);
  const reset = useCallback(() => {
    txReset();
    setTxDetails(null);
  }, [txReset]);
  useEffect(() => {
    if (validatorAddressProp)
      setValidatorAddress(validatorAddressProp);
  }, [validatorAddressProp]);
  return {
    gas,
    loadingStatus,
    txStatus,
    txDetails,
    canSubmit,
    formErrors,
    asset,
    delegation,
    validatorAddress,
    setValidatorAddress,
    submit,
    reset
  };
}

function useHoldings(asset) {
  const [allBalances, balancesStatus] = useBalances();
  const balances = useMemo(() => {
    if (!asset)
      return allBalances;
    const assetStr = [asset.id, asset.chain.name].join("-");
    return allBalances.filter((x) => {
      const itemStr = [x.asset.id, x.asset.chain.name].join("-");
      return itemStr === assetStr;
    });
  }, [asset, allBalances]);
  const [delegations, delegationsStatus] = useDelegations();
  const [stakedBalancesBase, rewardBalancesBase, unbondingBalancesBase] = useMemo(() => {
    return [
      OnomyHub.Utils.combineBalances(delegations.map((d) => d.delegated)),
      OnomyHub.Utils.combineBalances(delegations.map((d) => d.reward)),
      OnomyHub.Utils.combineBalances(delegations.map((d) => d.unbonding))
    ];
  }, [delegations]);
  const [stakedBalances, stakedStatus] = useDetailedBalances(stakedBalancesBase);
  const [rewardBalances, rewardStatus] = useDetailedBalances(rewardBalancesBase);
  const [unbondingBalances, unbondingStatus] = useDetailedBalances(unbondingBalancesBase);
  const [totals, totalsStatus] = useBalanceTotals(useMemo(() => {
    return [...balances, ...stakedBalances, ...rewardBalances, ...unbondingBalances];
  }, [balances, stakedBalances, rewardBalances, unbondingBalances]));
  const nomAvailable = useMemo(() => {
    const nom = balances.find(OnomyHubConstants.isAssetNOM);
    if (!nom)
      return OnomyHubConstants.UNKNOWN_BALANCE;
    return nom;
  }, [balances]);
  const nomTotal = useMemo(() => {
    const nom = totals.find(OnomyHubConstants.isAssetNOM);
    if (!nom)
      return OnomyHubConstants.UNKNOWN_BALANCE;
    return nom;
  }, [totals]);
  const nomStaked = useMemo(() => {
    const nom = stakedBalances.find(OnomyHubConstants.isAssetNOM);
    if (!nom)
      return OnomyHubConstants.UNKNOWN_BALANCE;
    return nom;
  }, [stakedBalances]);
  const nomUnbonding = useMemo(() => {
    const nom = unbondingBalances.find(OnomyHubConstants.isAssetNOM);
    if (!nom)
      return OnomyHubConstants.UNKNOWN_BALANCE;
    return nom;
  }, [unbondingBalances]);
  const nomRewards = useMemo(() => {
    const nom = rewardBalances.find(OnomyHubConstants.isAssetNOM);
    if (!nom)
      return OnomyHubConstants.UNKNOWN_BALANCE;
    return nom;
  }, [rewardBalances]);
  const ethTotal = useMemo(() => {
    const eth = totals.find(OnomyHubConstants.isAssetETH);
    if (!eth)
      return OnomyHubConstants.UNKNOWN_BALANCE;
    return eth;
  }, [totals]);
  const bNomTotal = useMemo(() => {
    const bnom = totals.find(OnomyHubConstants.isAssetBNOM);
    if (!bnom)
      return OnomyHubConstants.UNKNOWN_BALANCE;
    return bnom;
  }, [totals]);
  const balanceTotalFiat = useFiatTotal(balances);
  const stakedTotalFiat = useFiatTotal(stakedBalances);
  const rewardTotalFiat = useFiatTotal(rewardBalances);
  const unbondingTotalFiat = useFiatTotal(unbondingBalances);
  const grandTotalFiat = balanceTotalFiat + stakedTotalFiat + rewardTotalFiat + unbondingTotalFiat;
  const loadingStatus = useAsyncStatuses([
    balancesStatus,
    delegationsStatus,
    stakedStatus,
    rewardStatus,
    unbondingStatus,
    totalsStatus
  ]);
  return {
    loadingStatus,
    delegations,
    balances,
    stakedBalances,
    rewardBalances,
    unbondingBalances,
    totals,
    balanceTotalFiat,
    stakedTotalFiat,
    rewardTotalFiat,
    unbondingTotalFiat,
    grandTotalFiat,
    nomAvailable,
    nomTotal,
    ethTotal,
    bNomTotal,
    nomStaked,
    nomUnbonding,
    nomRewards
  };
}

function useBuyBridgeNomForm() {
  const { hub } = useOnomyHub();
  const [attemptTx, txStatus, resetTxStatus] = useAsyncProcess();
  const [ethBidAmountFixed, setEthBidAmountFixed] = useState("");
  const [slippagePoints, setSlippagePoints] = useState(1e4);
  const [ethBalance, ethBalanceStatus] = useAssetBalance(OnomyHubConstants.ETH_ASSET);
  const [txDetails, setTxDetails] = useState(null);
  const [bNomAskAmountFixed, bNomRawQuoteStatus] = useAsyncPoll(useCallback(async () => {
    const bigNum = new BigNumber(ethBidAmountFixed);
    if (!hub || bigNum.isNaN() || bigNum.lte(0))
      return "0";
    return hub.BuyBridgeNom.quote({ ethBidAmountFixed });
  }, [hub, ethBidAmountFixed]), "0");
  const [bNomAskAmount, bNomQuoteDetailsStatus] = useDetailedBalance(OnomyHubConstants.BNOM_ASSET, bNomAskAmountFixed);
  const bNomQuoteStatus = useAsyncStatuses([bNomRawQuoteStatus, bNomQuoteDetailsStatus]);
  const [ethBidAmount, ethBidAmountDetailsStatus] = useDetailedBalance(OnomyHubConstants.ETH_ASSET, ethBidAmountFixed);
  const inputParams = useMemo(() => ({
    ethBidAmountFixed,
    bNomAskAmountFixed,
    slippagePoints
  }), [ethBidAmountFixed, bNomAskAmountFixed, slippagePoints]);
  const gas = useGasForm(OnomyHubConstants.ETH_ASSET, OnomyHubConstants.ETH_ASSET, async () => {
    if (!hub)
      return "0";
    return hub.BuyBridgeNom.estimateGas(inputParams);
  }, [hub, inputParams]);
  const ethTotalAmount = useDetailedBalance(OnomyHubConstants.ETH_ASSET, (ethBidAmount.amount + gas.gasAmount.amount).toString());
  const loadingStatus = useAsyncStatuses([ethBalanceStatus]);
  const formErrors = useMemo(() => {
    return hub ? hub.BuyBridgeNom.getParamsErrors(inputParams, ethBalance.amount.toString(), gas.gasAmount.amount.toString()) : null;
  }, [hub, inputParams, ethBalance.amount, gas.gasAmount.amount]);
  const canSubmit = useMemo(() => {
    return !formErrors && loadingStatus.finished && bNomQuoteStatus.finished;
  }, [loadingStatus, bNomQuoteStatus, formErrors]);
  const doPurchase = useCallback(() => {
    return attemptTx(async () => {
      if (!hub || !canSubmit)
        return;
      const res = await hub.BuyBridgeNom.execute({
        ...inputParams,
        gasMultiplier: gas.gasPrice
      });
      setTxDetails(res);
    });
  }, [attemptTx, hub, canSubmit, inputParams, gas.gasPrice]);
  const reset = useCallback(() => {
    setEthBidAmountFixed("");
    setSlippagePoints(1e4);
    resetTxStatus();
    setTxDetails(null);
  }, [resetTxStatus, setEthBidAmountFixed]);
  return {
    loadingStatus,
    txStatus,
    bNomQuoteStatus,
    ethBidAmountDetailsStatus,
    ethBalance,
    ethBidAmount,
    ethTotalAmount,
    bNomAskAmount,
    gas,
    formErrors,
    canSubmit,
    ethBidAmountFixed,
    slippagePoints,
    txDetails,
    setEthBidAmountFixed,
    setSlippagePoints,
    doPurchase,
    reset
  };
}

const DEFAULT_ETH_STATE = {
  isTradingEnabled: false,
  blockNumber: new BigNumber(0),
  currentETHPrice: new BigNumber(0),
  currentNOMPrice: new BigNumber(0),
  NOMallowance: new BigNumber(0),
  supplyNOM: new BigNumber(0)
};
function useBondingCurveState({ ethBridgeWaitBlocks = 35 }) {
  var _a;
  const [ethState, setEthState] = useState(DEFAULT_ETH_STATE);
  const ethBlockNumRef = useRef(ethState.blockNumber);
  ethBlockNumRef.current = ethState.blockNumber;
  const [bNomSupply, bNomSupplyStatus] = useDetailedBalance(OnomyHubConstants.BNOM_ASSET, ethState.supplyNOM.toFixed(0));
  const { hub, wallet } = useOnomyHub();
  const [onomyAddress, setOnomyAddress] = useState("");
  const [nomBalance, nomBalanceStatus, nomBalanceRef] = useAssetBalance(OnomyHubConstants.NOM_ASSET, useMemo(() => {
    return {
      chain: OnomyHubConstants.ONOMY_CHAIN,
      id: onomyAddress
    };
  }, [onomyAddress]));
  const [ethBalance, ethBalanceStatus] = useAssetBalance(OnomyHubConstants.ETH_ASSET);
  const [bNomBalance, bNomBalanceStatus] = useAssetBalance(OnomyHubConstants.BNOM_ASSET);
  const [onomyWalletAddr, hubStatus] = useAsyncPoll(useCallback(async () => {
    var _a2;
    const addr = await (hub == null ? void 0 : hub.getAssetAddress(OnomyHubConstants.NOM_ASSET));
    return (_a2 = addr == null ? void 0 : addr.id) != null ? _a2 : "";
  }, [hub]), "");
  const isOnomyAddressValid = useIsAddressValid({
    chain: OnomyHubConstants.ONOMY_CHAIN,
    id: onomyAddress
  });
  const [ethBlockchainAddr, ethStatus] = useAddress(OnomyHubConstants.ETH_ASSET);
  const ethAddress = (_a = ethBlockchainAddr == null ? void 0 : ethBlockchainAddr.id) != null ? _a : "";
  useEffect(() => {
    if (onomyWalletAddr)
      setOnomyAddress(onomyWalletAddr);
  }, [onomyWalletAddr]);
  const [bNomSellAllowance, allowanceStatus] = useDetailedBalance(OnomyHubConstants.BNOM_ASSET, ethState.NOMallowance.toFixed(0));
  const bNomPerEth = useMemo(() => 1 / ethState.currentETHPrice.shiftedBy(-18).toNumber(), [ethState.currentETHPrice]);
  useEffect(() => {
    var _a2;
    if (!hub)
      return;
    async function onBlock(bNumber) {
      if (!hub)
        return;
      const blockNumber = new BigNumber(bNumber);
      if (blockNumber.eq(ethBlockNumRef.current))
        return;
      try {
        const newState = await hub.BondingCurve.getState();
        setEthState({
          blockNumber,
          ...newState
        });
      } catch (e) {
        console.error("Failed Chain Promise", e);
      }
    }
    (_a2 = hub.chains.ethereum.signer.provider) == null ? void 0 : _a2.on("block", onBlock);
    onBlock(1);
    return () => {
      var _a3;
      (_a3 = hub.chains.ethereum.signer.provider) == null ? void 0 : _a3.removeListener("block", onBlock);
    };
  }, [hub]);
  const status = useAsyncStatuses([
    bNomSupplyStatus,
    nomBalanceStatus,
    ethBalanceStatus,
    bNomBalanceStatus,
    hubStatus,
    ethStatus,
    allowanceStatus
  ]);
  return {
    ...ethState,
    bNomSupply,
    bNomPerEth,
    async logout() {
      await Promise.all([wallet.ethereum.disconnect(), wallet.onomy.disconnect()]);
      window.location.reload();
    },
    ethActive: !!(hub == null ? void 0 : hub.chains.ethereum.signer),
    onomyClient: hub == null ? void 0 : hub.chains.onomy.client,
    status,
    bondingCurve: hub == null ? void 0 : hub.BondingCurve,
    onomyAddress,
    isOnomyAddressValid,
    ethAddress,
    nomBalance,
    ethBalance,
    bNomBalance,
    bNomSellAllowance,
    setOnomyAddress
  };
}
const BondingCurveContext = createContext(DEFAULT_ETH_STATE);
const useBondingCurve = () => useContext(BondingCurveContext);
function BondingCurveProvider({
  children,
  ethBridgeWaitBlocks = 35
}) {
  const state = useBondingCurveState({ ethBridgeWaitBlocks });
  return /* @__PURE__ */ React.createElement(BondingCurveContext.Provider, {
    value: state
  }, children);
}

function useAnomSupplyFetchCb() {
  const { onomyClient } = useBondingCurve();
  return useCallback(async () => {
    if (!onomyClient)
      return new BigNumber(0);
    return onomyClient.getAnomSupply();
  }, [onomyClient]);
}
function useAnomSupply() {
  return useAsyncPoll(useAnomSupplyFetchCb(), new BigNumber(0));
}

gql`
  query transactions($filter: String!, $skipped: Int!, $first: Int, $from: Int, $to: Int) {
    bnomhistoricalFrames(
      first: $first
      skip: $skipped
      where: { type: $filter, startTime_lte: $to }
      orderBy: startTime
      orderDirection: desc
    ) {
      startTime
      endTime
      startPrice
      endPrice
      minPrice
      maxPrice
    }
  }
`;
const PERIOD_MAP = {
  "1": "Minute",
  "15": "QuarterHour",
  "60": "Hour",
  "480": "QuarterDay",
  "1D": "Day",
  "1W": "Week"
};
const NOM_SYMBOL_INFO = {
  name: "bNOM",
  full_name: "bNOM",
  description: "bNOM",
  exchange: "EthBondCurve",
  listed_exchange: "EthBondCurve",
  format: "price",
  has_intraday: true,
  has_no_volume: true,
  has_seconds: false,
  has_ticks: false,
  has_daily: true,
  has_empty_bars: true,
  data_status: "streaming",
  original_currency_code: "ETH",
  currency_code: "ETH",
  supported_resolutions: Object.keys(PERIOD_MAP),
  intraday_multipliers: Object.keys(PERIOD_MAP),
  ticker: "bNOM",
  timezone: "Etc/UTC",
  type: "crypto",
  session: "24x7",
  minmov: 1,
  pricescale: 1e10
};
function formatPrice(price) {
  return new BigNumber$1(price).shiftedBy(-18).toFixed(10);
}
class OnomyBondingTradeData {
  constructor(apolloClient, web3Library) {
    this.lastBlock = 0;
    this.apolloClient = apolloClient;
    this.web3Library = web3Library;
    this.barSubs = {};
    this.lastTick = {};
    this.onBlockMined = this.onBlockMined.bind(this);
    this.web3Library.on("block", this.onBlockMined);
  }
  async onBlockMined(bnum) {
    if (bnum <= this.lastBlock)
      return;
    this.lastBlock = bnum;
    const subs = Object.values(this.barSubs);
    for (const { symbolInfo, resolution, onTick } of subs) {
      this.getBars(symbolInfo, resolution, { countBack: 1, from: 0, to: 0, firstDataRequest: false }, (res) => {
        const last = res.pop();
        if (last)
          onTick(last);
      }, (err) => console.error(err));
    }
  }
  destroy() {
    this.web3Library.removeListener("block", this.onBlockMined);
  }
  onReady(cb) {
    setTimeout(() => cb({
      exchanges: [
        {
          value: NOM_SYMBOL_INFO.exchange,
          name: NOM_SYMBOL_INFO.exchange,
          desc: NOM_SYMBOL_INFO.exchange
        }
      ],
      symbols_types: [
        {
          value: NOM_SYMBOL_INFO.type,
          name: NOM_SYMBOL_INFO.type
        }
      ],
      supported_resolutions: NOM_SYMBOL_INFO.supported_resolutions,
      currency_codes: ["ETH"],
      units: {},
      supports_marks: false,
      supports_timescale_marks: false,
      supports_time: true
    }), 0);
  }
  async searchSymbols(userInput, exchange, symbolType, onResult) {
    onResult([
      {
        symbol: NOM_SYMBOL_INFO.ticker,
        full_name: NOM_SYMBOL_INFO.name,
        description: NOM_SYMBOL_INFO.description,
        exchange: NOM_SYMBOL_INFO.exchange,
        ticker: NOM_SYMBOL_INFO.ticker,
        type: NOM_SYMBOL_INFO.type
      }
    ]);
  }
  resolveSymbol(symbolName, onSuccess) {
    setTimeout(() => onSuccess(NOM_SYMBOL_INFO), 0);
  }
  async getBars(symbolInfo, resolution, periodParams, onResult, onError) {
    try {
      const res = await fetch(`/trade-history/bnom/eth/${PERIOD_MAP[resolution] || "Hour"}?from=${periodParams.from}&to=${periodParams.to}&countBack=${periodParams.countBack}&block=${this.lastBlock}`);
      const resp = await res.json();
      const {
        data: { bnomhistoricalFrames: frames }
      } = resp;
      const bars = frames.slice().reverse().map((frame) => {
        const time = parseInt(frame.startTime, 10) * 1e3;
        if (!this.lastTick[resolution] || this.lastTick[resolution] < time) {
          this.lastTick[resolution] = time;
        }
        return {
          time,
          open: formatPrice(frame.startPrice),
          high: formatPrice(frame.maxPrice),
          low: formatPrice(frame.minPrice),
          close: formatPrice(frame.endPrice),
          volume: 0
        };
      });
      if (bars.length > 0) {
        onResult(bars, { noData: false });
      } else {
        onResult([], { noData: true });
      }
    } catch (e) {
      console.error(e);
      onError(`${e}`);
    }
  }
  subscribeBars(symbolInfo, resolution, onTick, listenerGuid, onResetCacheNeededCallback) {
    this.barSubs[listenerGuid] = {
      symbolInfo,
      resolution,
      onTick,
      listenerGuid,
      onResetCacheNeededCallback
    };
  }
  unsubscribeBars(listenerGuid) {
    delete this.barSubs[listenerGuid];
  }
}

function useOnomyBondingTradeData(graphQlEndpoint) {
  const { hub } = useOnomyHub();
  const library = hub == null ? void 0 : hub.chains.ethereum.signer.provider;
  const apolloClient = useMemo(() => new ApolloClient({
    uri: graphQlEndpoint,
    cache: new InMemoryCache()
  }), [graphQlEndpoint]);
  try {
    return useMemo(() => {
      if (!library || !apolloClient)
        return null;
      return new OnomyBondingTradeData(apolloClient, library);
    }, [library, apolloClient]);
  } catch (e) {
    return null;
  }
}

function useSellBridgeNomForm() {
  const { hub } = useOnomyHub();
  const { bNomSellAllowance } = useBondingCurve();
  const [attemptTx, txStatus, txReset] = useAsyncProcess();
  const [attemptApproveTx, approveTxStatus, approveTxReset] = useAsyncProcess();
  const [bNomAskAmountFixedRaw, setBNomAskAmountFixedRaw] = useState("");
  const [bNomApproveFixedRaw, setBNomApproveFixedRaw] = useState("0");
  const [slippagePoints, setSlippagePoints] = useState(1e4);
  const [isMaxAmount, setIsMaxAmount] = useState(false);
  const [isMaxApproveAmount, setIsMaxApproveAmount] = useState(false);
  const [txDetails, setTxDetails] = useState(null);
  const [approveTxDetails, setApproveTxDetails] = useState(null);
  const [bNomBalance, bNomBalanceStatus] = useAssetBalance(OnomyHubConstants.BNOM_ASSET);
  const bNomAskAmountFixed = useMemo(() => isMaxAmount ? bNomBalance.amount.toString() : bNomAskAmountFixedRaw, [isMaxAmount, bNomAskAmountFixedRaw, bNomBalance.amount]);
  const bNomApproveFixed = useMemo(() => isMaxApproveAmount ? bNomBalance.amount.toString() : bNomApproveFixedRaw, [isMaxApproveAmount, bNomApproveFixedRaw, bNomBalance.amount]);
  const [ethBidAmountFixed, ethRawQuoteStatus] = useAsyncPoll(useCallback(async () => {
    const bigNum = new BigNumber(bNomAskAmountFixed);
    if (!hub || bigNum.isNaN() || bigNum.lte(0))
      return "0";
    return hub.SellBridgeNom.quote({ bNomAskAmountFixed });
  }, [hub, bNomAskAmountFixed]), "0");
  const extraAllowanceNeededFixed = useMemo(() => {
    const diff = new BigNumber(bNomAskAmountFixed).minus(bNomSellAllowance.amount.toString());
    return diff.gt(0) ? diff.toFixed() : "";
  }, [bNomSellAllowance, bNomAskAmountFixed]);
  const [ethBidAmount, ethQuoteDetailsStatus] = useDetailedBalance(OnomyHubConstants.ETH_ASSET, ethBidAmountFixed);
  const ethQuoteStatus = useAsyncStatuses([ethRawQuoteStatus, ethQuoteDetailsStatus]);
  const [bNomApproveAmount, bNomApproveAmountDetailsStatus] = useDetailedBalance(OnomyHubConstants.BNOM_ASSET, bNomApproveFixed);
  const [bNomAskAmount, bNomAskAmountDetailsStatus] = useDetailedBalance(OnomyHubConstants.BNOM_ASSET, bNomAskAmountFixed);
  const [extraAllowanceNeeded, extraAllowanceNeededStatus] = useDetailedBalance(OnomyHubConstants.BNOM_ASSET, extraAllowanceNeededFixed);
  const inputParams = useMemo(() => ({
    ethBidAmountFixed,
    bNomAskAmountFixed,
    slippagePoints
  }), [ethBidAmountFixed, bNomAskAmountFixed, slippagePoints]);
  const gas = useGasForm(OnomyHubConstants.ETH_ASSET, OnomyHubConstants.ETH_ASSET, async () => {
    if (!hub)
      return "0";
    return hub.SellBridgeNom.estimateGas(inputParams);
  }, [hub, inputParams, bNomSellAllowance.amount]);
  const loadingStatus = useAsyncStatuses([bNomBalanceStatus]);
  const formErrors = useMemo(() => {
    return hub ? hub.SellBridgeNom.getParamsErrors(inputParams, bNomBalance.amount.toString()) : null;
  }, [hub, inputParams, bNomBalance.amount]);
  const canSubmit = useMemo(() => {
    return !formErrors && loadingStatus.finished && extraAllowanceNeededStatus.finished && ethQuoteStatus.finished;
  }, [loadingStatus, ethQuoteStatus, extraAllowanceNeededStatus, formErrors]);
  const toggleMaxAmount = useCallback(() => setIsMaxAmount((x) => !x), []);
  const toggleMaxApproveAmount = useCallback(() => setIsMaxApproveAmount((x) => !x), []);
  const setBNomAskAmountFixed = useCallback((amt) => {
    setIsMaxAmount(false);
    setBNomAskAmountFixedRaw(amt);
  }, []);
  const setBNomApproveFixed = useCallback((amt) => {
    setIsMaxApproveAmount(false);
    setBNomApproveFixedRaw(amt);
  }, []);
  const doSell = useCallback(() => {
    return attemptTx(async () => {
      if (!hub || !canSubmit)
        return;
      const res = await hub.SellBridgeNom.execute({
        ...inputParams,
        gasMultiplier: gas.gasPrice
      });
      setTxDetails(res);
    });
  }, [attemptTx, hub, canSubmit, inputParams, gas.gasPrice]);
  const doApprove = useCallback(() => {
    return attemptApproveTx(async () => {
      if (!hub)
        return;
      const res = await hub.SellBridgeNom.approve(bNomApproveFixed, gas.gasPrice);
      setApproveTxDetails(res);
    });
  }, [attemptApproveTx, bNomApproveFixed, gas.gasPrice, hub]);
  const resetApprove = useCallback(() => {
    setBNomApproveFixed("0");
    approveTxReset();
    setApproveTxDetails(null);
  }, [approveTxReset, setBNomApproveFixed]);
  const reset = useCallback(() => {
    setBNomAskAmountFixed("");
    setSlippagePoints(1e4);
    setIsMaxAmount(false);
    txReset();
    resetApprove();
    setTxDetails(null);
  }, [resetApprove, setBNomAskAmountFixed, txReset]);
  useEffect(() => {
    if (!extraAllowanceNeededFixed)
      return;
    const current = new BigNumber(bNomApproveFixed);
    if (!current.eq(extraAllowanceNeededFixed)) {
      setBNomApproveFixed(extraAllowanceNeededFixed);
    }
  }, [extraAllowanceNeededFixed]);
  return {
    loadingStatus,
    txStatus,
    approveTxStatus,
    txDetails,
    approveTxDetails,
    ethQuoteStatus,
    bNomAskAmountDetailsStatus,
    bNomApproveAmountDetailsStatus,
    extraAllowanceNeededStatus,
    bNomBalance,
    bNomSellAllowance,
    ethBidAmount,
    bNomAskAmount,
    bNomApproveAmount,
    extraAllowanceNeeded,
    isMaxAmount,
    isMaxApproveAmount,
    gas,
    formErrors,
    canSubmit,
    bNomAskAmountFixed,
    slippagePoints,
    bNomApproveFixed,
    extraAllowanceNeededFixed,
    setBNomAskAmountFixed,
    setBNomApproveFixed,
    setSlippagePoints,
    toggleMaxAmount,
    toggleMaxApproveAmount,
    doSell,
    doApprove,
    reset,
    resetApprove
  };
}

function useBridgeForm({
  fromAsset = OnomyHubConstants.BNOM_ASSET,
  toAsset = OnomyHubConstants.NOM_ASSET
} = {}) {
  var _a, _b, _c, _d, _e, _f, _g;
  const { hub } = useOnomyHub();
  const bridge = useMemo(() => {
    var _a2, _b2;
    const allBridges = (_a2 = hub == null ? void 0 : hub.bridges) != null ? _a2 : [];
    const res = (_b2 = allBridges.find((x) => OnomyHubConstants.isAssetEqual(x.getFromAsset(), fromAsset) && OnomyHubConstants.isAssetEqual(x.getToAsset(), toAsset))) != null ? _b2 : null;
    return res;
  }, [hub == null ? void 0 : hub.bridges, fromAsset, toAsset]);
  const hasApproveStep = useMemo(() => {
    var _a2;
    return (_a2 = bridge == null ? void 0 : bridge.getRequiresApproval()) != null ? _a2 : false;
  }, [bridge]);
  const [bridgeAllowance, bridgeAllowanaceDetailsStatus] = useSubscribedValue(bridge == null ? void 0 : bridge.onApprovedAmountUpdate, OnomyHubConstants.UNKNOWN_BALANCE);
  const isFromChainConnected = useConnectedWallet(fromAsset.chain);
  const isToChainConnected = useConnectedWallet(toAsset.chain);
  const [bridgeStatus, setBridgeStatus] = useState(null);
  const [attemptTx, txStatus, txReset] = useAsyncProcess();
  const [attemptApproveTx, approveTxStatus, approveTxReset] = useAsyncProcess();
  const [fromBalance, fromNomBalanceStatus] = useAssetBalance(fromAsset);
  const [toBalance, nomBalanceStatus] = useAssetBalance(toAsset);
  const [toAddress] = useAddress(toAsset);
  const [bridgeAmountFixed, setBridgeAmountFixedRaw] = useState("");
  const [bridgeApproveFixed, setBridgeApproveFixedRaw] = useState("0");
  const [isMaxAmount, setIsMaxAmount] = useState(false);
  const [isMaxApproveAmount, setIsMaxApproveAmount] = useState(false);
  const [txDetails, setTxDetails] = useState(null);
  const [approveTxDetails, setApproveTxDetails] = useState(null);
  const approveInputParams = useMemo(() => ({
    amount: {
      asset: fromAsset,
      amount: BigInt(bridgeApproveFixed)
    },
    gasPrice: "1"
  }), [fromAsset, bridgeApproveFixed]);
  const txInputParams = useMemo(() => ({
    destination: toAddress,
    amount: {
      asset: fromAsset,
      amount: BigInt(bridgeAmountFixed)
    },
    gasPrice: "1"
  }), [bridgeAmountFixed, fromAsset, toAddress]);
  const [txMaxParams] = useAsyncValue(useCallback(async () => {
    if (!bridge)
      return txInputParams;
    return bridge.calculateMax(txInputParams);
  }, [bridge, txInputParams]), txInputParams);
  const [approveMaxParams] = useAsyncValue(useCallback(async () => {
    if (!bridge)
      return approveInputParams;
    return bridge.calculateMaxApprove(approveInputParams);
  }, [bridge, approveInputParams]), approveInputParams);
  const approveGas = useGasForm((_a = bridge == null ? void 0 : bridge.getGasAsset()) != null ? _a : OnomyHubConstants.NOM_ASSET, (_b = bridge == null ? void 0 : bridge.getGasAsset()) != null ? _b : OnomyHubConstants.NOM_ASSET, async () => {
    if (!bridge || !bridge.getRequiresApproval())
      return "0";
    return bridge.estimateApproveGas(approveInputParams);
  }, [bridge, approveInputParams]);
  const txGas = useGasForm((_c = bridge == null ? void 0 : bridge.getGasAsset()) != null ? _c : OnomyHubConstants.NOM_ASSET, (_d = bridge == null ? void 0 : bridge.getGasAsset()) != null ? _d : OnomyHubConstants.NOM_ASSET, async () => {
    if (!bridge)
      return "0";
    return bridge.estimateTxGas(txInputParams);
  }, [bridge, txInputParams]);
  const approveParams = useMemo(() => {
    return {
      ...isMaxApproveAmount ? approveMaxParams : approveInputParams,
      gasPrice: approveGas.gasPrice
    };
  }, [approveInputParams, approveMaxParams, isMaxApproveAmount, approveGas.gasPrice]);
  const txParams = useMemo(() => {
    return {
      ...isMaxAmount ? txMaxParams : txInputParams,
      gasPrice: txGas.gasPrice
    };
  }, [txInputParams, txMaxParams, isMaxAmount, txGas.gasPrice]);
  const extraAllowanceNeededFixed = useMemo(() => {
    if (!(bridge == null ? void 0 : bridge.getRequiresApproval()))
      return "";
    const diff = new BigNumber(txParams.amount.amount.toString()).minus(bridgeAllowance.amount.toString());
    return diff.gt(0) ? diff.toFixed() : "";
  }, [bridge, bridgeAllowance, txParams.amount.amount]);
  const [extraAllowanceNeeded, extraAllowanceNeededStatus] = useDetailedBalance((_e = bridge == null ? void 0 : bridge.getFromAsset()) != null ? _e : OnomyHubConstants.BNOM_ASSET, extraAllowanceNeededFixed);
  const [bridgeAmount, bridgeAmountDetailsStatus] = useDetailedBalance((_f = bridge == null ? void 0 : bridge.getFromAsset()) != null ? _f : OnomyHubConstants.BNOM_ASSET, txParams.amount.amount.toString());
  const [bridgeApproveAmount, bridgeApproveAmountDetailsStatus] = useDetailedBalance((_g = bridge == null ? void 0 : bridge.getFromAsset()) != null ? _g : OnomyHubConstants.BNOM_ASSET, approveParams.amount.amount.toString());
  const [destinationAddress] = useAsyncValue(useCallback(async () => {
    var _a2;
    return (_a2 = bridge == null ? void 0 : bridge.getDestinationAddress(txParams)) != null ? _a2 : null;
  }, [bridge, txParams]), null);
  const loadingStatus = useAsyncStatuses([fromNomBalanceStatus, nomBalanceStatus]);
  const [txFormErrors] = useAsyncValue(useCallback(async () => {
    if (!bridge)
      return {};
    return bridge.validateTxParams(txParams);
  }, [bridge, txParams]), null);
  const [bridgeFee] = useAsyncValue(useCallback(async () => {
    if (!bridge)
      return OnomyHubConstants.UNKNOWN_BALANCE;
    return bridge.getBridgeFee();
  }, [bridge]), OnomyHubConstants.UNKNOWN_BALANCE);
  const [approveFormErrors] = useAsyncValue(useCallback(async () => {
    if (!bridge)
      return {};
    return bridge.validateApproveParams(approveParams);
  }, [bridge, approveParams]), null);
  const canSubmitApprove = useMemo(() => {
    return !approveFormErrors && loadingStatus.finished;
  }, [loadingStatus, approveFormErrors]);
  const canSubmitTx = useMemo(() => {
    return !txFormErrors && loadingStatus.finished && isFromChainConnected && isToChainConnected;
  }, [loadingStatus, txFormErrors, isFromChainConnected, isToChainConnected]);
  const toggleMaxAmount = useCallback(() => setIsMaxAmount((x) => !x), []);
  const toggleMaxApproveAmount = useCallback(() => setIsMaxApproveAmount((x) => !x), []);
  const setBridgeAmountFixed = useCallback((amt) => {
    setIsMaxAmount(false);
    setBridgeAmountFixedRaw(amt);
  }, []);
  const setBridgeApproveFixed = useCallback((amt) => {
    setIsMaxApproveAmount(false);
    setBridgeApproveFixedRaw(amt);
  }, []);
  const resetApprove = useCallback(() => {
    setBridgeApproveFixed("0");
    approveTxReset();
    setApproveTxDetails(null);
  }, [approveTxReset, setBridgeApproveFixed]);
  const reset = useCallback(() => {
    setBridgeAmountFixed("");
    setIsMaxAmount(false);
    txReset();
    resetApprove();
    setBridgeStatus(null);
    setTxDetails(null);
  }, [resetApprove, setBridgeAmountFixed, txReset]);
  const doBridge = useCallback(() => {
    return attemptTx(async () => {
      if (!canSubmitTx || !bridge)
        return;
      const [, completionPromise] = bridge.doBridge(txParams, setBridgeStatus);
      const res = await completionPromise;
      setTxDetails(res);
    });
  }, [attemptTx, canSubmitTx, bridge, txParams]);
  const doApprove = useCallback(() => {
    return attemptApproveTx(async () => {
      if (!bridge)
        return;
      const res = await bridge.doApprove(approveParams);
      setApproveTxDetails(res);
    });
  }, [attemptApproveTx, approveParams, bridge]);
  useEffect(() => {
    if (!extraAllowanceNeededFixed)
      return;
    const current = new BigNumber(bridgeApproveFixed);
    if (!current.eq(extraAllowanceNeededFixed)) {
      setBridgeApproveFixed(extraAllowanceNeededFixed);
    }
  }, [extraAllowanceNeededFixed]);
  return {
    loadingStatus,
    txStatus,
    approveTxStatus,
    txDetails,
    approveTxDetails,
    bridgeStatus,
    hasApproveStep,
    approveGas,
    txGas,
    bridge,
    bridgeFee,
    isFromChainConnected,
    isToChainConnected,
    bridgeAmountDetailsStatus,
    bridgeApproveAmountDetailsStatus,
    bridgeAllowanaceDetailsStatus,
    extraAllowanceNeededStatus,
    fromAsset,
    toAsset,
    fromBalance,
    toBalance,
    bridgeAllowance,
    bridgeAmount,
    bridgeApproveAmount,
    extraAllowanceNeeded,
    destinationAddress: isToChainConnected ? destinationAddress : null,
    isMaxApproveAmount,
    isMaxAmount,
    bridgeAmountFixed: isMaxAmount ? txParams.amount.amount.toString() : bridgeAmountFixed,
    bridgeApproveFixed: isMaxApproveAmount ? approveParams.amount.amount.toString() : bridgeApproveFixed,
    setBridgeApproveFixed,
    setBridgeAmountFixed,
    txFormErrors,
    approveFormErrors,
    canSubmitApprove,
    canSubmitTx,
    toggleMaxAmount,
    toggleMaxApproveAmount,
    reset,
    resetApprove,
    doApprove,
    doBridge
  };
}

function useMoveDelegationForm({
  srcValidatorAddressStr: srcValidatorAddressProp = "",
  dstValidatorAddressStr: dstValidatorAddressProp = ""
} = {}) {
  var _a;
  const { hub } = useOnomyHub();
  const [attempt, txStatus, txReset] = useAsyncProcess();
  const [srcValidatorAddress, setSrcValidatorAddress] = useState(srcValidatorAddressProp);
  const [dstValidatorAddress, setDstValidatorAddress] = useState(dstValidatorAddressProp);
  const [amountFixedRaw, setAmountFixedRaw] = useState("");
  const [isMaxAmount, setIsMaxAmount] = useState(false);
  const [txDetails, setTxDetails] = useState(null);
  const asset = OnomyHubConstants.NOM_ASSET;
  const gasAsset = asset;
  const [balance, balanceStatus] = useAssetBalance(asset);
  const [srcDelegation, srcDelegationStatus] = useDelegation({
    chain: OnomyHubConstants.ONOMY_CHAIN,
    id: srcValidatorAddress
  });
  const [dstDelegation, dstDelegationStatus] = useDelegation({
    chain: OnomyHubConstants.ONOMY_CHAIN,
    id: dstValidatorAddress
  });
  const [txAmount] = useDetailedBalance(asset, (_a = txDetails == null ? void 0 : txDetails.amountFixed) != null ? _a : "0");
  const amountFixed = useMemo(() => {
    var _a2;
    if (!isMaxAmount)
      return amountFixedRaw;
    return (_a2 = srcDelegation == null ? void 0 : srcDelegation.delegated.amount.toString()) != null ? _a2 : amountFixedRaw;
  }, [isMaxAmount, amountFixedRaw, srcDelegation == null ? void 0 : srcDelegation.delegated.amount]);
  const inputParams = useMemo(() => ({
    asset: OnomyHubConstants.NOM_ASSET,
    amountFixed,
    srcValidatorAddress,
    dstValidatorAddress
  }), [amountFixed, srcValidatorAddress, dstValidatorAddress]);
  const gas = useGasForm(inputParams.asset, gasAsset, async () => {
    if (!hub)
      return "0";
    return hub.MoveDelegation.estimateGas(inputParams);
  }, [hub, inputParams]);
  const maxParams = useMemo(() => {
    var _a2;
    return {
      ...inputParams,
      amountFixed: (_a2 = srcDelegation == null ? void 0 : srcDelegation.delegated.amount.toString()) != null ? _a2 : "0"
    };
  }, [inputParams, srcDelegation == null ? void 0 : srcDelegation.delegated.amount]);
  const params = isMaxAmount ? maxParams : inputParams;
  const formErrors = useMemo(() => {
    var _a2;
    return hub == null ? void 0 : hub.MoveDelegation.getParamsErrors(params, (_a2 = srcDelegation == null ? void 0 : srcDelegation.delegated.amount.toString()) != null ? _a2 : "0");
  }, [hub, params, srcDelegation == null ? void 0 : srcDelegation.delegated.amount]);
  const [[amount = OnomyHubConstants.UNKNOWN_BALANCE], amountStatus] = useDetailedBalances(useMemo(() => {
    return [
      {
        amount: BigInt(params.amountFixed),
        asset: asset || OnomyHubConstants.NOM_ASSET
      }
    ];
  }, [asset, params.amountFixed]));
  const loadingStatus = useAsyncStatuses([balanceStatus, srcDelegationStatus, dstDelegationStatus]);
  const canSubmit = useMemo(() => {
    return !formErrors && !loadingStatus.pending && !loadingStatus.error && !!gas.gasPrice;
  }, [formErrors, gas.gasPrice, loadingStatus]);
  const toggleMaxAmount = useCallback(() => setIsMaxAmount((x) => !x), []);
  const setAmountFixed = useCallback((amt) => {
    setIsMaxAmount(false);
    setAmountFixedRaw(amt);
  }, []);
  const reset = useCallback(() => {
    setAmountFixed("");
    setIsMaxAmount(false);
    txReset();
    setTxDetails(null);
  }, [setAmountFixed, txReset]);
  const submit = useCallback(() => {
    if (!hub || !canSubmit)
      return;
    attempt(async () => {
      var _a2;
      const res = await hub.MoveDelegation.execute({
        ...params,
        gasMultiplier: gas.gasPrice
      }, (_a2 = srcDelegation == null ? void 0 : srcDelegation.delegated.amount.toString()) != null ? _a2 : "0");
      setTxDetails(res);
    });
  }, [canSubmit, attempt, hub, params, gas.gasPrice, srcDelegation == null ? void 0 : srcDelegation.delegated.amount]);
  useEffect(() => {
    if (srcValidatorAddressProp)
      setSrcValidatorAddress(srcValidatorAddressProp);
  }, [srcValidatorAddressProp]);
  return {
    gas,
    formErrors,
    loadingStatus,
    txStatus,
    amountStatus,
    txDetails,
    canSubmit,
    asset,
    srcDelegation,
    dstDelegation,
    isMaxAmount,
    amount,
    balance,
    txAmount,
    srcValidatorAddress,
    dstValidatorAddress,
    amountFixed: params.amountFixed,
    setSrcValidatorAddress,
    setDstValidatorAddress,
    setAmountFixed,
    toggleMaxAmount,
    reset,
    submit
  };
}

function useGovVoteForm({ proposalId }) {
  var _a, _b, _c;
  const { hub } = useOnomyHub();
  const [attemptTx, txStatus, txReset] = useAsyncProcess();
  const [proposal, loadingStatus] = useProposal(proposalId);
  const [option, setOption] = useState((_b = (_a = proposal == null ? void 0 : proposal.myVote) == null ? void 0 : _a.option) != null ? _b : 0);
  const params = useMemo(() => {
    return {
      proposalId,
      option
    };
  }, [option, proposalId]);
  const txGas = useGasForm(OnomyHubConstants.NOM_ASSET, OnomyHubConstants.NOM_ASSET, async () => {
    if (!hub)
      return "0";
    const {
      amount: [fee]
    } = await hub.chains.onomy.client.feesForVote(params);
    return fee.amount;
  }, [hub, params]);
  const paramsWithGas = useMemo(() => {
    return {
      ...params,
      feeMultiplier: parseFloat(txGas.gasPrice)
    };
  }, [txGas.gasPrice, params]);
  useEffect(() => {
    var _a2, _b2;
    if (typeof ((_a2 = proposal == null ? void 0 : proposal.myVote) == null ? void 0 : _a2.option) !== "undefined")
      setOption((_b2 = proposal == null ? void 0 : proposal.myVote) == null ? void 0 : _b2.option);
  }, [(_c = proposal == null ? void 0 : proposal.myVote) == null ? void 0 : _c.option]);
  const reset = useCallback(() => {
    txReset();
  }, [txReset]);
  const submit = useCallback(() => attemptTx(async () => {
    if (!hub)
      return;
    await hub.chains.onomy.client.vote(paramsWithGas);
    await hub.proposals.forceGet(OnomyHubConstants.ONOMY_CHAIN.name, proposalId);
  }), [attemptTx, hub, paramsWithGas, proposalId]);
  return {
    txStatus,
    loadingStatus,
    proposal,
    txGas,
    option,
    setOption,
    reset,
    submit
  };
}

const TITLE_MAX_LENGTH = 140;
const DESCRIPTION_MAX_LENGTH = 2500;
function useSubmitProposalForm() {
  const asset = OnomyHubConstants.NOM_ASSET;
  const { hub } = useOnomyHub();
  const [attempt, txStatus, txReset] = useAsyncProcess();
  const [attemptDeposit, depositTxStatus, depositTxReset] = useAsyncProcess();
  const [proposalId, setProposalId] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [minDepositFixed] = useAsyncValue(useCallback(async () => {
    var _a;
    return (_a = hub == null ? void 0 : hub.chains.onomy.client.getMinSubmitProposalDeposit()) != null ? _a : "0";
  }, [hub]), "0");
  const [recipient, setRecipient] = useState("");
  const [amountFixed, setAmountFixed] = useState("");
  const [balance] = useAssetBalance(asset);
  const [amount, amountStatus] = useDetailedBalance(asset, amountFixed);
  const [minDepositAmount] = useDetailedBalance(asset, minDepositFixed);
  const inputParams = useMemo(() => {
    return {
      title,
      description,
      recipient,
      amountFixed: new BigNumber(amountFixed)
    };
  }, [title, description, recipient, amountFixed]);
  const gas = useGasForm(asset, asset, async () => {
    if (!hub)
      return "0";
    const res = await hub.chains.onomy.client.feesForSubmitProposal(inputParams);
    const [anom] = res.amount;
    return anom.amount;
  }, [hub, inputParams]);
  const formErrors = useMemo(() => {
    const params = inputParams;
    const errors = {};
    if (!params.title) {
      errors.title = "Title is required";
    } else if (params.title.length >= TITLE_MAX_LENGTH) {
      errors.title = `Title must be less than ${TITLE_MAX_LENGTH} characters`;
    }
    if (!params.description) {
      errors.description = "Description is required";
    } else if (params.description.length >= DESCRIPTION_MAX_LENGTH) {
      errors.description = `Description must be less than ${DESCRIPTION_MAX_LENGTH} characters`;
    }
    if (!params.recipient) {
      errors.recipient = "Recipient is required";
    } else if (!(hub == null ? void 0 : hub.chains.onomy.isAddressValid({
      chain: OnomyHubConstants.ONOMY_CHAIN,
      id: params.recipient
    }))) {
      errors.recipient = "Recipient must be a valid address";
    }
    if (!params.amountFixed) {
      errors.amountFixed = "Amount is required";
    } else {
      const amountFixedValue = new BigNumber(params.amountFixed);
      if (amountFixedValue.lte(0) || amountFixedValue.isNaN()) {
        errors.amountFixed = "Amount must be greater than 0";
      }
    }
    if (balance.amount < minDepositAmount.amount) {
      errors.balance = `You must have at least ${minDepositAmount.amountFormatted} ${minDepositAmount.asset.meta.symbol} for deposit`;
    }
    return Object.keys(errors).length > 0 ? errors : null;
  }, [
    inputParams,
    hub == null ? void 0 : hub.chains.onomy,
    balance.amount,
    minDepositAmount.amount,
    minDepositAmount.amountFormatted,
    minDepositAmount.asset.meta.symbol
  ]);
  const canSubmit = useMemo(() => {
    return !formErrors;
  }, [formErrors]);
  const reset = useCallback(() => {
    setTitle("");
    setDescription("");
    setRecipient("");
    setAmountFixed("");
    setProposalId("");
    txReset();
    depositTxReset();
  }, [txReset, depositTxReset]);
  const submit = useCallback(() => {
    return attempt(async () => {
      if (!hub)
        return;
      const pId = await hub.chains.onomy.client.submitProposal({
        ...inputParams,
        feeMultiplier: parseInt(gas.gasPrice, 10)
      });
      if (pId)
        setProposalId(pId);
    });
  }, [hub, attempt, inputParams, gas]);
  const doDeposit = useCallback(() => {
    return attemptDeposit(async () => {
      if (!hub || !proposalId)
        return;
      await hub.chains.onomy.client.depositToProposal({
        proposalId,
        amountFixed: new BigNumber(minDepositFixed)
      });
    });
  }, [hub, attemptDeposit, minDepositFixed, proposalId]);
  return {
    TITLE_MAX_LENGTH,
    DESCRIPTION_MAX_LENGTH,
    gas,
    txStatus,
    depositTxStatus,
    amountStatus,
    proposalId,
    minDepositAmount,
    amount,
    title,
    description,
    amountFixed,
    recipient,
    canSubmit,
    formErrors,
    setTitle,
    setDescription,
    setAmountFixed,
    setRecipient,
    reset,
    submit,
    doDeposit
  };
}

function FixedAmountInput({ value, onChangeValue, meta, ...props }) {
  const [displayValue, setDisplayValue] = useFixedAmountInput({
    value,
    onChangeValue,
    meta
  });
  return /* @__PURE__ */ React.createElement("input", {
    ...props,
    type: "text",
    value: displayValue,
    onChange: (e) => setDisplayValue(e.target.value)
  });
}

const CurrencyFormattedBigNumber = ({
  value,
  maxDigitCount = 10,
  minDecimalPlaces = 2
}) => {
  const formatNumber = (number, decimalPlaces) => {
    const [beforeDecimal, afterDecimal] = number.toFixed(decimalPlaces).split(".");
    const beforeDec = beforeDecimal.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return afterDecimal ? [beforeDec, afterDecimal].join(".") : beforeDec;
  };
  const truncateDecimals = (number, maxDigits) => {
    let decimalPlaces = minDecimalPlaces;
    while (decimalPlaces >= 0) {
      const formattedNumber = formatNumber(number, decimalPlaces);
      if (formattedNumber.replaceAll(".", "").replaceAll(",", "").length <= maxDigits) {
        return formattedNumber;
      }
      decimalPlaces -= 1;
    }
    return null;
  };
  const summarizeNumber = (number) => {
    const units = ["thousand", "million", "billion", "trillion", "quadrillion"];
    let unitIndex = -1;
    let currentValue = number;
    const display = truncateDecimals(number, maxDigitCount);
    if (display && display.replaceAll(".", "").replaceAll(",", "").length <= maxDigitCount) {
      return display;
    }
    while (currentValue.gte(1e3)) {
      currentValue = currentValue.div(1e3);
      unitIndex += 1;
    }
    if (unitIndex === -1) {
      return truncateDecimals(number, maxDigitCount) || "INFINITY";
    }
    console.log("unitsIndex", unitIndex);
    const summary = truncateDecimals(currentValue, maxDigitCount - 2);
    return summary ? `${summary} ${units[unitIndex]}` : "INFINITY";
  };
  const formattedValue = summarizeNumber(value);
  return /* @__PURE__ */ React.createElement(React.Fragment, null, formattedValue);
};

function BigNumDisplay({
  value,
  decimalShift = 18,
  decimalPlaces = 6,
  maxDigitCount = 10
}) {
  const formatted = useMemo(() => new BigNumber$1(value).shiftedBy(-1 * decimalShift), [value, decimalShift]);
  return /* @__PURE__ */ React.createElement(CurrencyFormattedBigNumber, {
    value: formatted,
    minDecimalPlaces: decimalPlaces,
    maxDigitCount
  });
}

function SymbolDisplay({ symbol }) {
  return /* @__PURE__ */ React.createElement("sup", null, symbol);
}
function AmountDisplay({
  balance,
  decimalPlaces = 6,
  SymbolComponent = SymbolDisplay
}) {
  const { asset, amount } = balance;
  const { meta: assetMeta } = asset;
  const val = useMemo(() => new BigNumber(amount.toString()), [amount]);
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(BigNumDisplay, {
    value: val,
    decimalShift: asset.meta.decimalPlaces,
    decimalPlaces
  }), " ", SymbolComponent && /* @__PURE__ */ React.createElement(SymbolComponent, {
    symbol: assetMeta.symbol
  }));
}

function FiatValueDisplay({ value = 0, decimalPlaces = 2 }) {
  return /* @__PURE__ */ React.createElement(React.Fragment, null, "$", /* @__PURE__ */ React.createElement(CurrencyFormattedBigNumber, {
    value: new BigNumber(value),
    minDecimalPlaces: decimalPlaces
  }));
}

function RatioAsPercentage({ ratio }) {
  return /* @__PURE__ */ React.createElement(React.Fragment, null, (ratio * 100).toFixed(3), /* @__PURE__ */ React.createElement("sup", null, "%"));
}

function TruncatedAddress({ address }) {
  const { id } = address;
  return /* @__PURE__ */ React.createElement(React.Fragment, null, id.substring(0, 10), "...", id.substring(id.length - 4));
}

export { AmountDisplay, BigNumDisplay, BondingCurveContext, BondingCurveProvider, FiatValueDisplay, FixedAmountInput, OnomyHubContext, OnomyHubProvider, RatioAsPercentage, RequireOnomyHub, TruncatedAddress, useAddress, useAddresses, useAnomSupply, useAssetBalance, useBalanceTotals, useBalances, useBondingCurve, useBridgeForm, useBuyBridgeNomForm, useClaimRewardForm, useConnectedWallet, useDelegateAssetForm, useDelegation, useDelegations, useDetailedBalance, useDetailedBalances, useEthNetwork, useFiatTotal, useFixedAmountInput, useGasForm, useGovVoteForm, useHoldings, useIsAddressValid, useMoveDelegationForm, useOnomyBondingTradeData, useOnomyHub, useProposal, useProposals, useSellBridgeNomForm, useSubmitProposalForm, useSuggestedGasPrices, useSupportedAssetList, useTransferAssetForm, useWalletsForChain };
